import { AfterViewInit, Component, OnInit, ViewChild, signal } from '@angular/core';
import { AuthService, Block, BlockAbstractComponent, ParserService, ZoneService } from '@gallaghercp/core-frontend';
import { BreakpointObserver } from '@angular/cdk/layout';
import { isExternalLink } from '@core-app/utils/link.util';
import { Autoplay, Navigation, Pagination, Scrollbar } from 'swiper';
import { SwiperComponent, SwiperConfigInterface } from '@gallaghercp/core-swiper';
import { DomSanitizer } from '@angular/platform-browser';
import { APP_CONFIG } from '@core-app/app.config';
import { register } from 'swiper/element';

@Component({
    selector: 'core-block-hero-banner-split-feature',
    templateUrl: './block-hero-banner-split-feature.component.html',
    styleUrl: './block-hero-banner-split-feature.component.scss',
    standalone: false,
})
export class BlockHeroBannerSplitFeatureComponent extends BlockAbstractComponent implements OnInit, AfterViewInit {
    @ViewChild('homeBannerSwiper', { static: false }) homeBannerSwiper: SwiperComponent | undefined;

    backgroundImage?: string;
    isPaused = signal(false);
    private wasPlayingBeforeFocus = false;

    swiperConfig: SwiperConfigInterface = {
        modules: [Navigation, Pagination, Scrollbar, Autoplay],
        autoplay: { delay: 3000 },
        effect: 'fade',
        spaceBetween: 0,
        navigation: false,
        pagination: {
            el: '.swiper-pagination-bullets',
            clickable: true,
        },
        slidesPerView: 1,
    };

    constructor(
        protected override parser: ParserService,
        protected override auth: AuthService,
        protected breakpoint: BreakpointObserver,
        private sanitizer: DomSanitizer,
        private zoneService: ZoneService,
    ) {
        super(parser, auth);
    }

    override ngOnInit() {
        super.ngOnInit();

        // Set swiper config(s) that rely on CMS config
        // (can't set it at init because `this.block`
        // doesn't exist yet)
        this.swiperConfig.loop = this.block.data.options.loop ?? false;

        // Map CMS intervalSpeed (ms) to Swiper autoplay delay
        const interval = Number(this.block?.data?.options?.intervalSpeed);
        this.swiperConfig.autoplay = {
            delay: Number.isFinite(interval) && interval >= 500 && interval <= 60000 ? interval : 3000,
        };

        this.breakpoint.observe(['(max-width: 768px)']).subscribe(result => {
            if (result.matches) {
                if (this.block.data.image_mobile_url) {
                    this.backgroundImage = APP_CONFIG.frontend + this.block.data.image_mobile_url;
                } else {
                    // fallback incase we don't have a mobile image
                    this.backgroundImage = APP_CONFIG.frontend + this.block.data.image_url;
                }
            } else {
                this.backgroundImage = APP_CONFIG.frontend + this.block.data.image_url;
            }
        });
    }

    moveSlidesPrev() {
        this.homeBannerSwiper?.directiveRef?.prevSlide();
    }

    moveSlidesNext() {
        this.homeBannerSwiper?.directiveRef?.nextSlide();
    }

    toggleAutoplay() {
        if (this.isPaused()) {
            this.homeBannerSwiper?.directiveRef?.startAutoplay();
            this.isPaused.set(false);
        } else {
            this.homeBannerSwiper?.directiveRef?.stopAutoplay();
            this.isPaused.set(true);
        }
    }

    onFocusIn() {
        if (!this.isPaused()) {
            this.wasPlayingBeforeFocus = true;
            this.homeBannerSwiper?.directiveRef?.stopAutoplay();
        } else {
            this.wasPlayingBeforeFocus = false;
        }
    }

    onFocusOut() {
        if (this.wasPlayingBeforeFocus && !this.isPaused()) {
            this.homeBannerSwiper?.directiveRef?.startAutoplay();
        }
        this.wasPlayingBeforeFocus = false;
    }

    ngAfterViewInit(): void {
        register();
    }

    getBackground() {
        switch (this.block.data.bgFillColor) {
            case 'white':
                return 'white';

            case 'primary':
                return 'var(--color-primary)';

            case 'secondary':
                return 'var(--color-secondary)';

            case 'gradient':
                return `linear-gradient(${this.block.data.bgLinearGradient})`;
        }

        return 'white';
    }

    isExternalLink(block: Block): boolean {
        const url = block.data.link;

        // If we have external link set as true on the CMS side, always return true.
        if (block.data.isExternalLink) return true;

        return isExternalLink(url);
    }

    getRouterLink(url: string): string {
        return this.zoneService.injectZoneCodeUrl(url);
    }
}
